import React from 'react';
import { Action, withInject } from '@piwikpro/platform';
import {
  Stack,
  Text,
  withTranslation,
  HelpLink,
  Spinner,
} from '@piwikpro/ui-components';
import { AuthSettings } from '@piwikpro/auth-crate';
import { Formik } from 'formik';
import FailMessage from '../FailMessage';

export interface TwoFactorAuthenticationProps {
  t: (key: string) => string
  enforced2fa: boolean
  isLoading: boolean
  failed: boolean
  authSettings: AuthSettings
  samlEnabled?: boolean
}
const fieldName = 'two_factor_authentication';

interface SwitchHandlerValue {
  [fieldName]: boolean
}

const TwoFactorAuthentication: React.FunctionComponent<TwoFactorAuthenticationProps> = ({
  t,
  enforced2fa,
  isLoading,
  failed,
  authSettings,
  samlEnabled,
}) => {
  const handleSubmitSwitcher = async (
    values: SwitchHandlerValue,
    { setSubmitting, setFieldValue }: Formik,
  ) => {
    try {
      await authSettings.set2faSettingsForOrganization(values[fieldName]);
      await authSettings.getSettingsForOrganization();
    } catch (err) {
      setFieldValue(
        fieldName,
        !values[fieldName],
      );
    }
    setSubmitting(false);
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (failed) {
    return <FailMessage />;
  }

  return (
    <Stack vertical horizontalAlignment="middle">
      <Stack.Item fill>
        <Stack wrap verticalAlignment="center">
          <Stack.Item fill>
            <Stack vertical spacing="narrow">
              <Stack.Item>
                <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('accountSecurity.twoFactorAuth.enforce-two-factor-authentication.title')}</Text>
              </Stack.Item>
              <Stack.Item>
                <Text as="p">{t('accountSecurity.twoFactorAuth.enforce-two-factor-authentication.description')}</Text>
              </Stack.Item>
            </Stack>
          </Stack.Item>
          <Stack.Item>
            <Action.AutoSavedSwitcherControl
              id="enforce-two-factor-authentication"
              initialValue={enforced2fa}
              label={t('accountSecurity.twoFactorAuth.enforce-two-factor-authentication.title')}
              info={t('accountSecurity.twoFactorAuth.enforce-two-factor-authentication.description')}
              name={fieldName}
              onSubmit={handleSubmitSwitcher}
              disabledActionTooltipText={t('accountSecurity.twoFactorAuth.enforce-two-factor-authentication.block-reason')}
              disabled={samlEnabled}
            />
          </Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item>
        <HelpLink href="https://help.piwik.pro/support/account/two-factor-authentication/">
          Set up two-factor authentication (2FA)
        </HelpLink>
      </Stack.Item>
    </Stack>
  );
};

export default withInject({
  authSettings: 'AuthCrate.authSettings',
})(
  withTranslation(['administration'])(TwoFactorAuthentication),
);
